import { InjectFlags, Injectable, Injector } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    DetachedRouteHandle,
    ParamMap,
    PRIMARY_OUTLET,
    RouteReuseStrategy,
    Router,
} from '@angular/router';
import { TabService } from '@/app/core/services/tab.service';
import { TabRouteCacheRegistry } from '@/app/core/routing/tab-route-cache.registry';
import { normalizeTabRouteKey } from '@/app/core/routing/tab-route-key';

/**
 * Preserves routed component state when switching tabs: detaches the current route
 * only if its URL is still an open tab, and reattaches when navigating back.
 *
 * Only the primary-outlet leaf route participates in detach/store/attach — Angular
 * invokes reuse for each route level; handling parents would overwrite handles and
 * break caching (components recreated, HTTP runs again).
 *
 * Do not inject `Router` in the constructor — it depends on `RouteReuseStrategy` (NG0200).
 * Use `Injector.get(Router)` only in attach-phase methods where Router is fully built.
 */
@Injectable()
export class TabRouteReuseStrategy implements RouteReuseStrategy {
    constructor(
        private readonly injector: Injector,
        private readonly tabService: TabService,
        private readonly cache: TabRouteCacheRegistry,
    ) {}

    shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        return future.routeConfig === curr.routeConfig;
    }

    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        if (!this.isPrimaryLeafRoute(route)) {
            return false;
        }
        const key = this.cacheKeyForDeactivate(route);
        if (!this.isCacheableKey(key)) {
            return false;
        }
        return this.tabService.tabs().some((t) => normalizeTabRouteKey(t.route) === key);
    }

    store(_route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
        if (!this.isPrimaryLeafRoute(_route)) {
            return;
        }
        const key = this.cacheKeyForDeactivate(_route);
        if (!this.isCacheableKey(key)) {
            return;
        }
        this.cache.set(key, handle);
    }

    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        if (!this.isPrimaryLeafRoute(route)) {
            return false;
        }
        const key = this.cacheKeyForActivate(route);
        if (!this.isCacheableKey(key)) {
            return false;
        }
        return this.cache.has(key);
    }

    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        if (!this.isPrimaryLeafRoute(route)) {
            return null;
        }
        const key = this.cacheKeyForActivate(route);
        const handle = this.cache.get(key);
        if (!handle) {
            return null;
        }
        this.cache.delete(key);
        return handle;
    }

    /**
     * When deactivating, `Router.url` may already point at the *next* tab — do not use it.
     * Prefer internal RouterState URL (matches NavigationEnd) then snapshot build.
     */
    private cacheKeyForDeactivate(route: ActivatedRouteSnapshot): string {
        const tabKeys = new Set(this.tabService.tabs().map((t) => normalizeTabRouteKey(t.route)));
        const stateUrl = this.tryReadRouterStateSnapshotUrl(route);
        const nState = stateUrl ? normalizeTabRouteKey(stateUrl) : null;
        const nBuilt = normalizeTabRouteKey(this.buildUrlFromSnapshot(route));
        if (nState && tabKeys.has(nState)) {
            return nState;
        }
        if (tabKeys.has(nBuilt)) {
            return nBuilt;
        }
        return nBuilt;
    }

    /**
     * When activating, `Router.url` usually matches the tab we are restoring — safe to align.
     */
    private cacheKeyForActivate(route: ActivatedRouteSnapshot): string {
        const tabKeys = new Set(this.tabService.tabs().map((t) => normalizeTabRouteKey(t.route)));
        const router = this.injector.get(Router, null, InjectFlags.Optional);
        if (router?.url) {
            const n = normalizeTabRouteKey(router.url);
            if (tabKeys.has(n)) {
                return n;
            }
        }
        return this.cacheKeyForDeactivate(route);
    }

    /** Same RouterState URL Angular uses for NavigationEnd / TabItem.route. */
    private tryReadRouterStateSnapshotUrl(route: ActivatedRouteSnapshot): string | null {
        const root = route.root as unknown as { _routerState?: { snapshot?: { url?: string } } };
        const url = root._routerState?.snapshot?.url;
        return url != null && url.length > 0 ? url : null;
    }

    private isPrimaryLeafRoute(route: ActivatedRouteSnapshot): boolean {
        if (route.outlet !== PRIMARY_OUTLET) {
            return false;
        }
        if (route.firstChild) {
            return false;
        }
        const cfg = route.routeConfig;
        if (!cfg) {
            return false;
        }
        return !!(cfg.loadComponent || cfg.component);
    }

    /** Matches `TabItem.route` / `NavigationEnd#urlAfterRedirects` for the primary outlet tree. */
    private buildUrlFromSnapshot(route: ActivatedRouteSnapshot): string {
        const parts: string[] = [];
        let n: ActivatedRouteSnapshot | null = route.root;
        while (n) {
            for (const u of n.url) {
                if (u.path.length > 0) {
                    parts.push(u.path);
                }
            }
            n = n.firstChild;
        }
        let path = parts.length > 0 ? '/' + parts.join('/') : '/';
        path = path.replace(/\/+/g, '/');
        const qs = this.serializeQueryParams(route.root.queryParamMap);
        return qs.length > 0 ? `${path}?${qs}` : path;
    }

    private serializeQueryParams(qm: ParamMap): string {
        if (!qm.keys.length) {
            return '';
        }
        return qm.keys
            .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(qm.get(k) ?? '')}`)
            .join('&');
    }

    private isCacheableKey(normalizedKey: string): boolean {
        const pathOnly = normalizedKey.split('?')[0] ?? normalizedKey;
        if (pathOnly.startsWith('/auth')) {
            return false;
        }
        if (pathOnly === '/no-connection' || pathOnly.startsWith('/no-connection/')) {
            return false;
        }
        return true;
    }
}
